import React, { useState, useEffect } from 'react';
import { Form, Input, Radio, Button, Row, Col, message } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import MomentDatePicker from './MomentDatePicker.tsx';

const PatientIntakeDemographics = ({ sessionId, initialData, onSave, onNext }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      // Convert date string back to moment object for DatePicker
      const formData = { ...initialData };
      if (formData.dateOfBirth) {
        formData.dateOfBirth = moment(formData.dateOfBirth);
      }
      form.setFieldsValue(formData);
    }
  }, [initialData, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    
    // Convert moment object to ISO string for API
    const formattedValues = {
      ...values,
      dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null
    };

    const success = await onSave(formattedValues);
    if (success) {
      // Scroll to top before moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onNext();
    }
    setLoading(false);
  };

  const validateAge = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('intake.demographics.validation.dateOfBirthRequired')));
    }

    const age = moment().diff(value, 'years');
    if (age < 0) {
      return Promise.reject(new Error(t('intake.demographics.validation.dateOfBirthFuture')));
    }
    if (age > 120) {
      return Promise.reject(new Error(t('intake.demographics.validation.dateOfBirthInvalid')));
    }
    
    return Promise.resolve();
  };

  const validatePhone = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('intake.demographics.validation.phoneRequired')));
    }

    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length < 10) {
      return Promise.reject(new Error(t('intake.demographics.validation.phoneInvalid')));
    }
    
    return Promise.resolve();
  };

  return (
    <div className="intake-form-section">
      <h3>{t('intake.demographics.title')}</h3>
      <p>{t('intake.demographics.description')}</p>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        autoComplete="off"
        size="large"
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="lastName"
              label={t('intake.demographics.lastName')}
              rules={[
                { required: true, message: t('intake.demographics.validation.lastNameRequired') },
                { min: 2, message: t('intake.demographics.validation.lastNameMinLength') },
                { max: 50, message: t('intake.demographics.validation.lastNameMaxLength') }
              ]}
            >
              <Input placeholder={t('intake.demographics.placeholders.lastName')} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="middleName"
              label={t('intake.demographics.middleName')}
              rules={[
                { max: 50, message: t('intake.demographics.validation.middleNameMaxLength') }
              ]}
            >
              <Input placeholder={t('intake.demographics.placeholders.middleName')} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              name="firstName"
              label={t('intake.demographics.firstName')}
              rules={[
                { required: true, message: t('intake.demographics.validation.firstNameRequired') },
                { min: 2, message: t('intake.demographics.validation.firstNameMinLength') },
                { max: 50, message: t('intake.demographics.validation.firstNameMaxLength') }
              ]}
            >
              <Input placeholder={t('intake.demographics.placeholders.firstName')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="dateOfBirth"
              label={t('intake.demographics.dateOfBirth')}
              rules={[
                { required: true, message: t('intake.demographics.validation.dateOfBirthRequired') },
                { validator: validateAge }
              ]}
            >
              <MomentDatePicker
                style={{ width: '100%' }}
                placeholder={t('intake.demographics.placeholders.dateOfBirth')}
                format="MM/DD/YYYY"
                disabledDate={(current) => current && current > moment().endOf('day')}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="sex"
              label={t('intake.demographics.sex')}
              rules={[{ required: true, message: t('intake.demographics.validation.sexRequired') }]}
            >
              <Radio.Group>
                <Radio value="Male">{t('intake.demographics.male')}</Radio>
                <Radio value="Female">{t('intake.demographics.female')}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name={['contactInfo', 'phone']}
              label={t('intake.demographics.phoneNumber')}
              rules={[
                { required: true, message: t('intake.demographics.validation.phoneRequired') },
                { validator: validatePhone }
              ]}
            >
              <Input placeholder={t('intake.demographics.placeholders.phoneNumber')} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name={['contactInfo', 'email']}
              label={t('intake.demographics.emailAddress')}
              rules={[
                { required: true, message: t('intake.demographics.validation.emailRequired') },
                { type: 'email', message: t('intake.demographics.validation.emailInvalid') },
                { max: 100, message: t('intake.demographics.validation.emailMaxLength') }
              ]}
            >
              <Input placeholder={t('intake.demographics.placeholders.emailAddress')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="relationshipStatus"
          label={t('intake.demographics.relationshipStatus')}
        >
          <Radio.Group>
            <Radio value="Single">{t('intake.demographics.single')}</Radio>
            <Radio value="Married">{t('intake.demographics.married')}</Radio>
            <Radio value="Divorced">{t('intake.demographics.divorced')}</Radio>
            <Radio value="Widowed">{t('intake.demographics.widowed')}</Radio>
            <Radio value="Separated">{t('intake.demographics.separated')}</Radio>
            <Radio value="Domestic Partnership">{t('intake.demographics.domesticPartnership')}</Radio>
            <Radio value="Other">{t('intake.demographics.other')}</Radio>
          </Radio.Group>
        </Form.Item>

        <div className="intake-form-buttons">
          <div></div> {/* Empty div for spacing */}
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
          >
            {t('intake.demographics.saveAndContinue')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PatientIntakeDemographics;
